<template>
  <div class="project-container">
    <!-- Hero Section -->
    <header class="hero-section">
      <div class="hero-content">
        <div class="hero-badge">Nuit de l'Info 2025</div>
        <h1 class="hero-title">NDI 2025 Front</h1>
        <p class="hero-subtitle">Plateforme événementielle & Jeu Interactif</p>
        <div class="hero-meta">
          <span class="meta-item"><i class="mdi mdi-calendar"></i> 2025</span>
          <span class="meta-item"><i class="mdi mdi-account-group"></i> Équipe de 10</span>
          <span class="meta-item"><i class="mdi mdi-school"></i> Mentorat Tech</span>
        </div>
      </div>
      <div class="hero-image">
        <img :src="require('@/assets/image/ndi-home.png')" alt="NDI 2025 Home"/>
      </div>
    </header>

    <!-- Overview Card -->
    <section class="card overview-card">
      <div class="card-header">
        <i class="mdi mdi-information-outline"></i>
        <h2>Vue d'ensemble</h2>
      </div>
      <div class="card-content">
        <p class="lead-text">
          Ce projet a été réalisé dans le cadre de la <strong>Nuit de l'Info 2025</strong>. Il s'agit d'une application Web Fullstack complète développée par une équipe de <strong>10 étudiants</strong>.
        </p>
        <p>
          En tant qu'étudiant de <strong>3ème année</strong>, j'ai occupé un rôle de <strong>Lead Tech / Mentor</strong> pour l'équipe Frontend. Une grande partie de mon travail a consisté à former les autres membres sur les technologies modernes utilisées (Frameworks, TypeScript) et à les accompagner dans le développement.
        </p>
        <div class="tech-stack">
          <span class="tech-badge">Next.js 16</span>
          <span class="tech-badge">React 19</span>
          <span class="tech-badge">TypeScript</span>
          <span class="tech-badge">Tailwind CSS 4</span>
          <span class="tech-badge">Docker</span>
          <span class="tech-badge">Kubernetes</span>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="card features-card">
      <div class="card-header">
        <i class="mdi mdi-gamepad-variant"></i>
        <h2>Fonctionnalités Clés</h2>
      </div>
      <div class="card-content">
        <div class="capability-list">
          <div class="capability-item">
            <i class="mdi mdi-snake"></i>
            <span><strong>Jeu Snake Intégré :</strong> Version web avec assets personnalisés et tableau des scores.</span>
          </div>
          <div class="capability-item">
            <i class="mdi mdi-robot"></i>
            <span><strong>PhiloBot (IA) :</strong> Chatbot "Philosophe absurde" propulsé par l'API Groq (LLM).</span>
          </div>
        </div>
      </div>
    </section>

    <!-- IUT Competencies Validation -->
    <section class="card validation-card">
      <div class="card-header">
        <i class="mdi mdi-certificate-outline"></i>
        <h2>Validation des Compétences IUT</h2>
      </div>
      <div class="card-content">
        <div class="validation-grid">
          <div class="validation-item">
            <div class="v-header">
              <span class="v-code">AC 4</span>
              <h4>Collaborer : Management de Projet</h4>
            </div>
            <p><strong>Pourquoi :</strong> Organiser le travail d'une équipe de 4 développeurs frontend sous une deadline extrême de 24h.</p>
            <p><strong>Comment :</strong> Rôle de <strong>Lead Tech</strong>, définition de l'architecture, répartition des tâches et mentorat sur React/Next.js.</p>
          </div>

          <div class="validation-item">
            <div class="v-header">
              <span class="v-code">CE6.04</span>
              <h4>Collaborer : Communication Efficace</h4>
            </div>
            <p><strong>Pourquoi :</strong> Assurer la synchronisation entre les équipes Frontend, Backend et Graphisme pour une intégration fluide.</p>
            <p><strong>Comment :</strong> Mise en place de points de synchronisation réguliers et utilisation d'outils collaboratifs en temps réel.</p>
          </div>

          <div class="validation-item">
            <div class="v-header">
              <span class="v-code">UE5.1</span>
              <h4>Réaliser : Adaptation Supports</h4>
            </div>
            <p><strong>Pourquoi :</strong> Offrir une expérience utilisateur optimale sur tous les terminaux (web et mobile).</p>
            <p><strong>Comment :</strong> Développement d'une interface ultra-responsive avec <strong>Tailwind CSS 4</strong> et optimisation des Server Components.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Learning Outcomes -->
    <section class="card learning-card">
      <div class="card-header">
        <i class="mdi mdi-school-outline"></i>
        <h2>Compétences & Apprentissages</h2>
      </div>
      <div class="card-content">
        <div class="learning-grid">
          <div class="learning-category">
            <h4>Leadership Technique</h4>
            <ul>
              <li>Mentorat d'équipe junior</li>
              <li>Code Review & Best Practices</li>
            </ul>
          </div>
          <div class="learning-category">
            <h4>Frontend Next-Gen</h4>
            <ul>
              <li>Next.js 16 & Server Actions</li>
              <li>React 19 & Zustand</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import {Vue, Component} from 'vue-facing-decorator';

@Component({})
export default class NDIComponent extends Vue {
}
</script>

<style scoped>
.project-container {
  font-family: 'Inter', 'Segoe UI', sans-serif;
  line-height: 1.8;
  color: var(--text-color);
  background-color: var(--background-color);
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  padding-left: 100px;
}

.hero-section {
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 40px;
  margin-bottom: 60px;
  padding: 60px 40px;
  background: linear-gradient(135deg, var(--card-background) 0%, var(--dark-bg-secondary) 100%);
  border-radius: 24px;
  border: 1px solid var(--dark-border);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}

.hero-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.hero-badge {
  display: inline-block;
  width: fit-content;
  padding: 8px 16px;
  background: rgba(19, 124, 139, 0.2);
  color: var(--primary-color);
  border-radius: 20px;
  font-size: 0.85em;
  font-weight: 600;
  margin-bottom: 16px;
  border: 1px solid var(--primary-color);
}

.hero-title {
  font-size: 3.5em;
  font-weight: 800;
  color: var(--h1-color);
  margin: 0 0 16px 0;
  background: linear-gradient(135deg, #ffffff 0%, var(--tertiary-color) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.hero-subtitle {
  font-size: 1.4em;
  color: var(--sub-color);
  margin-bottom: 24px;
}

.hero-meta {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-muted);
}

.meta-item i {
  color: var(--primary-color);
}

.hero-image {
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-image img {
  max-width: 100%;
  height: auto;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(19, 124, 139, 0.3);
}

.card {
  background: var(--card-background);
  border-radius: 20px;
  padding: 40px;
  margin-bottom: 32px;
  border: 1px solid var(--dark-border);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
}

.card:hover {
  transform: translateY(-4px);
  border-color: var(--primary-color);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 28px;
  padding-bottom: 20px;
  border-bottom: 2px solid var(--dark-border);
}

.card-header i {
  font-size: 32px;
  color: var(--primary-color);
}

.card-header h2 {
  font-size: 2em;
  color: var(--h2-color);
  margin: 0;
}

.tech-stack {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 24px;
}

.tech-badge {
  padding: 10px 20px;
  background: rgba(19, 124, 139, 0.15);
  color: var(--primary-color);
  border-radius: 12px;
  font-size: 0.9em;
  font-weight: 600;
  border: 1px solid rgba(19, 124, 139, 0.3);
}

.capability-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.capability-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: var(--dark-bg-secondary);
  border-radius: 12px;
  border-left: 3px solid var(--primary-color);
}

.capability-item i {
  font-size: 24px;
  color: var(--primary-color);
}

/* Validation Card Styles */
.validation-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
}

.validation-item {
  padding: 24px;
  background: var(--dark-bg-secondary);
  border-radius: 16px;
  border: 1px solid var(--dark-border);
  border-top: 4px solid var(--primary-color);
}

.v-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.v-code {
  padding: 4px 10px;
  background: var(--primary-color);
  color: #ffffff;
  border-radius: 6px;
  font-weight: 800;
  font-size: 0.9em;
  white-space: nowrap;
}

.v-header h4 {
  margin: 0;
  color: var(--h2-color);
  font-size: 1.1em;
}

.validation-item p {
  font-size: 0.95em;
  margin: 8px 0;
  line-height: 1.6;
}

.validation-item strong {
  color: var(--primary-color);
}

.learning-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
}

.learning-category h4 {
  color: var(--primary-color);
}

.learning-category ul {
  list-style: none;
  padding: 0;
}

.learning-category li::before {
  content: '→';
  color: var(--primary-color);
  margin-right: 8px;
}

@media (max-width: 768px) {
  .project-container { padding-left: 20px; }
  .hero-section { grid-template-columns: 1fr; }
}
</style>